import type { ApiClient } from '../services/api-client.js';
import type { FCMClient } from '../services/fcm-client.js';
import type { StorageService } from '../services/storage.js';

export class UserRegistration {
	constructor(
		private apiClient: ApiClient,
		private fcmClient: FCMClient,
		private storageService: StorageService,
		private onSuccess: () => void,
	) {}

	/**
	 * Render the registration form
	 */
	render(container: HTMLElement): void {
		container.innerHTML = `
			<div class="registration-form">
				<h2>Welcome to Notify-X</h2>
				<p class="subtitle">Enter your Bitbucket username to get started</p>

				<form id="registration-form">
					<div class="form-group">
						<input
							type="text"
							id="username-input"
							placeholder="Bitbucket username"
							autocomplete="username"
							required
						/>
					</div>

					<div class="notification-permission-info">
						<p>📱 We'll request notification permission after registration</p>
					</div>

					<button type="submit" id="register-button">
						Register
					</button>

					<div id="error-message" class="error-message hidden"></div>
					<div id="loading" class="loading hidden">Registering...</div>
				</form>
			</div>
		`;

		this.attachEventListeners();
	}

	/**
	 * Attach event listeners to form
	 */
	private attachEventListeners(): void {
		const form = document.getElementById('registration-form') as HTMLFormElement;
		const usernameInput = document.getElementById('username-input') as HTMLInputElement;
		const registerButton = document.getElementById('register-button') as HTMLButtonElement;

		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			const username = usernameInput.value.trim();

			if (!username) {
				this.showError('Please enter your Bitbucket username');
				return;
			}

			await this.handleRegistration(username, registerButton);
		});
	}

	/**
	 * Handle user registration
	 */
	private async handleRegistration(username: string, button: HTMLButtonElement): Promise<void> {
		this.hideError();
		this.showLoading(true);
		button.disabled = true;

		try {
			// Step 1: Register user (without FCM token initially)
			console.log('Registering user:', username);
			const user = await this.apiClient.registerUser({
				bitbucket_username: username,
			});

			console.log('User registered:', user);

			// Save user data
			this.storageService.setUserId(user.id);
			this.storageService.setUsername(user.bitbucket_username);

			// Step 2: Request notification permission and get FCM token
			if (this.fcmClient.isSupported()) {
				try {
					console.log('Requesting notification permission...');
					const token = await this.fcmClient.requestPermissionAndGetToken();

					if (token) {
						console.log('FCM token received, updating user...');
						this.storageService.setFcmToken(token);

						// Update user with FCM token
						await this.apiClient.updateFcmToken(user.id, token);
						console.log('FCM token updated successfully');
					} else {
						console.warn('No FCM token received, continuing without notifications');
					}
				} catch (fcmError) {
					console.error('Failed to set up notifications:', fcmError);
					// Continue anyway - user is registered
				}
			} else {
				console.warn('Push notifications not supported in this browser');
			}

			// Success!
			this.showLoading(false);
			this.onSuccess();
		} catch (error) {
			console.error('Registration failed:', error);
			this.showError(
				error instanceof Error ? error.message : 'Registration failed. Please try again.',
			);
			this.showLoading(false);
			button.disabled = false;
		}
	}

	/**
	 * Show error message
	 */
	private showError(message: string): void {
		const errorElement = document.getElementById('error-message');
		if (errorElement) {
			errorElement.textContent = message;
			errorElement.classList.remove('hidden');
		}
	}

	/**
	 * Hide error message
	 */
	private hideError(): void {
		const errorElement = document.getElementById('error-message');
		if (errorElement) {
			errorElement.classList.add('hidden');
		}
	}

	/**
	 * Show/hide loading indicator
	 */
	private showLoading(show: boolean): void {
		const loadingElement = document.getElementById('loading');
		if (loadingElement) {
			if (show) {
				loadingElement.classList.remove('hidden');
			} else {
				loadingElement.classList.add('hidden');
			}
		}
	}
}
